
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment";

import { formatDate, parseDate } from "../ProgrammationManager/Helpers/dateFormatter";

import { useDatePicker } from "@/Hooks/UseDatePicker";
import useTranslation from "@/Hooks/UseTranslation";

import Button from '@/Components/Button/Button'

import 'react-datepicker/dist/react-datepicker.css'
import "./ModalDateRangePicker.scss";

const ModalDateRangePicker = () => {

  const { t } = useTranslation()

  const {
    show,
    mode,
    startDate,
    endDate,
    projectStartDate,
    projectEndDate,
    nonWorkingDays,
    onChangeRange,
    hideDatePicker,
  } = useDatePicker()

  const [draftStart, setDraftStart] = useState<Date | null>(null)
  const [draftEnd, setDraftEnd] = useState<Date | null>(null)

  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 576 : true
  )

  const [monthsShown, setMonthsShown] = useState<number>(() => (isMobile ? 1 : 2))


  const monthDiff = (a: Date, b: Date) =>
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1

  const projStartDate = projectStartDate ? parseDate(projectStartDate) : undefined
  const projEndDate = projectEndDate ? parseDate(projectEndDate) : undefined

  const nonWorkingSet = useMemo(() => new Set(nonWorkingDays || []), [nonWorkingDays])

  const maxMonths = projStartDate && projEndDate ? monthDiff(projStartDate, projEndDate) : 2

  const totalMonths = Math.max(1, maxMonths)
  const visibleMonths = Math.min(monthsShown, totalMonths)

  useEffect(() => {

    const startMoment = startDate && moment(startDate, 'YYYY-MM-DD').isValid()
      ? moment(startDate, 'YYYY-MM-DD')
      : moment()
    const endMoment = endDate && moment(endDate, 'YYYY-MM-DD').isValid()
      ? moment(endDate, 'YYYY-MM-DD')
      : moment().add(1, 'day')

    setDraftStart(startMoment.toDate())
    setDraftEnd(endMoment.toDate())
  }, [startDate, endDate])

  //onresize window
  useEffect(() => {
    if (typeof window === 'undefined') return

    const onResize = () => setIsMobile(window.innerWidth < 576)

    // ensure initial state is correct
    onResize()

    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [])

  // if isMobile changes, update monthsShown
  useEffect(() => {
    setMonthsShown(isMobile ? 1 : 2)
  }, [isMobile])

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setDraftStart(start)
    setDraftEnd(end)
  }

  const handleCancel = () => {
    setDraftStart(parseDate(startDate))
    setDraftEnd(parseDate(endDate))
    hideDatePicker?.()
  }

  const handleContinue = () => {
    if (!draftStart || !draftEnd) return

    let finalStart = draftStart
    let finalEnd = draftEnd

    if (finalStart.getTime() > finalEnd.getTime()) {
      finalStart = draftEnd
      finalEnd = draftStart
    }

    onChangeRange({
      startDate: formatDate(finalStart),
      endDate: formatDate(finalEnd),
    })
  }

  return (
    <Modal
      show={show && mode === 'range'}
      className="modal-date-range-picker"
      backdropClassName='modal-date-range-picker-backdrop'
      centered
      onHide={hideDatePicker}
    >
      <Modal.Body>
        <div className="daterangepicker-container">
          <DatePicker
            inline
            selectsRange
            filterDate={(date) => !nonWorkingSet.has(formatDate(date))}
            monthsShown={visibleMonths}
            wrapperClassName="input-date-range__wrapper"
            calendarClassName="input-date-range__calendar"
            className="input-date-range__root"
            startDate={draftStart}
            endDate={draftEnd}
            minDate={projStartDate || undefined}
            maxDate={projEndDate || undefined}
            openToDate={draftStart || projStartDate || moment().toDate()}
            onChange={handleDateChange}
            renderCustomHeader={({
              monthDate,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => {
              const prevDisabled =
                prevMonthButtonDisabled ||
                (projStartDate ? monthDate <= projStartDate : false)

              const nextDisabled =
                nextMonthButtonDisabled ||
                (projEndDate ? monthDate >= projEndDate : false)

              return (
                <div className="d-flex justify-content-between align-items-center px-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={decreaseMonth}
                    disabled={prevDisabled}
                  >
                    ‹
                  </Button>

                  <span className="font-weight-bold">
                    {monthDate.toLocaleString('es-PE', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={increaseMonth}
                    disabled={nextDisabled}
                  >
                    ›
                  </Button>
                </div>
              )
            }}
          />
          <div className="buttons">
            <Button
              variant="secondary"
              onClick={handleCancel}
            >
              {t('DATEPICKER:CancelButton')}
            </Button>
            <Button
              variant="primary"
              onClick={handleContinue}
            >
              {t('DATEPICKER:AcceptButton')}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ModalDateRangePicker;
